import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import EDatePicker from '@/components/everridge/EDatePicker';
import ESingleSelectPicker from '@/components/everridge/ESingleSelectPicker';
import { cn } from '@/lib/utils';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { CalendarEventType, DocumentType } from '@/features/calendar/types/calendar-dto';
import {
    BookingStatus,
    SuperAdminBookingListItemDto,
    UpdateSuperAdminBookingRequestDto,
} from '@/features/superAdmin/features/data/types/superAdmin-data.types';
import {
    useSuperAdminDataApartmentsLookupApi,
    useSuperAdminDataHotelsLookupApi,
} from '@/features/superAdmin/features/data/shared/hooks/useSuperAdminDataLookupsApi';
import {
    SuperAdminBookingEditFormData,
    SuperAdminBookingEditFormSchema,
} from '@/features/superAdmin/features/data/bookings/validators/SuperAdminBookingEditFormValidator';
import {
    getBookingStatusLabel,
    getDocumentTypeLabel,
    getEventTypeLabel,
} from '@/features/superAdmin/features/data/bookings/utils/superAdminBookingEnumLabels';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    booking: SuperAdminBookingListItemDto;
    isMobile?: boolean;
    isSaving?: boolean;
    onSave: (body: UpdateSuperAdminBookingRequestDto) => Promise<void>;
};

const parseDateOnly = (value: string): Date => {
    const [year, month, day] = value.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
};

const SuperAdminBookingEditDialog: FC<Props> = ({
    open,
    onOpenChange,
    booking,
    isMobile,
    isSaving,
    onSave,
}) => {
    const { t } = useTranslation();
    const [step, setStep] = useState<'form' | 'confirm'>('form');
    const [confirmText, setConfirmText] = useState('');
    const [pendingBody, setPendingBody] = useState<UpdateSuperAdminBookingRequestDto | null>(null);
    const confirmWord = t('superAdmin.data.bookings.edit.confirmWord');

    const { control, handleSubmit, watch, setValue, register } = useForm<SuperAdminBookingEditFormData>({
        resolver: zodResolver(SuperAdminBookingEditFormSchema),
        values: {
            hotelId: booking.hotelId,
            apartmentId: booking.apartmentId ?? null,
            checkInDate: parseDateOnly(booking.checkInDate),
            checkOutDate: parseDateOnly(booking.checkOutDate),
            guestFullName: booking.guestFullName ?? null,
            remarks: booking.remarks ?? null,
            eventType: booking.eventType,
            documentType: booking.documentType,
            bookingStatus: booking.bookingStatus,
            numberOfRooms: booking.numberOfRooms,
            totalAmount: booking.totalAmount,
            isActive: booking.isActive,
            hasAccommodation: booking.hasAccommodation,
            documentNumber: booking.documentNumber ?? null,
            documentDate: new Date(booking.documentDate),
            externalId: booking.externalId ?? null,
        },
    });

    const hotelIdValue = watch('hotelId');
    const apartmentIdValue = watch('apartmentId');
    const eventTypeValue = watch('eventType');
    const documentTypeValue = watch('documentType');
    const bookingStatusValue = watch('bookingStatus');
    const isActiveValue = watch('isActive');
    const hasAccommodationValue = watch('hasAccommodation');

    const { data: hotelsData } = useSuperAdminDataHotelsLookupApi(booking.tenantId);
    const { data: apartmentsData } = useSuperAdminDataApartmentsLookupApi(
        booking.tenantId,
        undefined,
        hotelIdValue,
    );

    const hotelSelectOptions = useMemo(
        () =>
            (hotelsData?.items ?? []).map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [hotelsData?.items],
    );

    const apartmentSelectOptions = useMemo(
        () =>
            (apartmentsData?.items ?? []).map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [apartmentsData?.items],
    );

    const eventTypeOptions = useMemo(
        () =>
            [CalendarEventType.Reservation, CalendarEventType.Accommodation, CalendarEventType.HouseUse].map(
                (value) => ({
                    value: String(value),
                    label: getEventTypeLabel(value, t),
                }),
            ),
        [t],
    );

    const documentTypeOptions = useMemo(
        () =>
            [DocumentType.Reservation, DocumentType.Accommodation].map((value) => ({
                value: String(value),
                label: getDocumentTypeLabel(value, t),
            })),
        [t],
    );

    const bookingStatusOptions = useMemo(
        () =>
            [
                BookingStatus.Pending,
                BookingStatus.New,
                BookingStatus.Ongoing,
                BookingStatus.Cancelled,
            ].map((value) => ({
                value: String(value),
                label: getBookingStatusLabel(value, t),
            })),
        [t],
    );

    const resetDialogState = () => {
        setStep('form');
        setConfirmText('');
        setPendingBody(null);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            resetDialogState();
        }
        onOpenChange(nextOpen);
    };

    const onSubmitForm = (data: SuperAdminBookingEditFormData) => {
        const body: UpdateSuperAdminBookingRequestDto = {
            hotelId: data.hotelId,
            apartmentId: data.apartmentId,
            checkInDate: DateTimeHelper.getDateOnly(data.checkInDate)!,
            checkOutDate: DateTimeHelper.getDateOnly(data.checkOutDate)!,
            guestFullName: data.guestFullName?.trim() || null,
            remarks: data.remarks?.trim() || null,
            eventType: data.eventType,
            documentType: data.documentType,
            bookingStatus: data.bookingStatus,
            numberOfRooms: data.numberOfRooms,
            totalAmount: data.totalAmount,
            isActive: data.isActive,
            hasAccommodation: data.hasAccommodation,
            documentNumber: data.documentNumber?.trim() || null,
            documentDate: data.documentDate.toISOString(),
            externalId: data.externalId?.trim() || null,
        };
        setPendingBody(body);
        setStep('confirm');
    };

    const handleConfirmSave = async () => {
        if (!pendingBody) return;
        await onSave(pendingBody);
        resetDialogState();
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className={cn(isMobile && 'max-h-[90vh] overflow-y-auto', 'sm:max-w-2xl')}>
                <DialogHeader>
                    <DialogTitle>
                        {step === 'form'
                            ? t('superAdmin.data.bookings.edit.title')
                            : t('superAdmin.data.bookings.edit.confirmTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {step === 'form'
                            ? t('superAdmin.data.bookings.edit.description')
                            : t('superAdmin.data.bookings.edit.confirmDescription')}
                    </DialogDescription>
                </DialogHeader>

                {step === 'form' ? (
                    <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="flex flex-col gap-1.5">
                                <Label>{t('superAdmin.data.bookings.columns.hotel')}</Label>
                                <ESingleSelectPicker
                                    value={hotelIdValue}
                                    onChange={(value) => {
                                        setValue('hotelId', value ?? '');
                                        setValue('apartmentId', null);
                                    }}
                                    options={hotelSelectOptions}
                                    placeholder={t('superAdmin.data.filters.hotelPlaceholder')}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label>{t('superAdmin.data.bookings.columns.room')}</Label>
                                <ESingleSelectPicker
                                    value={apartmentIdValue}
                                    onChange={(value) => setValue('apartmentId', value)}
                                    options={apartmentSelectOptions}
                                    placeholder={t('superAdmin.data.filters.roomPlaceholder')}
                                    allOptionLabel={t('superAdmin.data.filters.allRooms')}
                                    disabled={!hotelIdValue}
                                />
                            </div>
                            <EDatePicker
                                control={control}
                                fieldName="checkInDate"
                                label={t('superAdmin.data.bookings.columns.checkIn')}
                            />
                            <EDatePicker
                                control={control}
                                fieldName="checkOutDate"
                                label={t('superAdmin.data.bookings.columns.checkOut')}
                            />
                            <EDatePicker
                                control={control}
                                fieldName="documentDate"
                                label={t('superAdmin.data.bookings.columns.date')}
                            />
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="documentNumber">
                                    {t('superAdmin.data.bookings.columns.number')}
                                </Label>
                                <Input id="documentNumber" {...register('documentNumber')} />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="guestFullName">
                                    {t('superAdmin.data.bookings.columns.guest')}
                                </Label>
                                <Input id="guestFullName" {...register('guestFullName')} />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="externalId">
                                    {t('superAdmin.data.bookings.columns.external')}
                                </Label>
                                <Input id="externalId" {...register('externalId')} />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label>{t('superAdmin.data.bookings.columns.event')}</Label>
                                <ESingleSelectPicker
                                    value={String(eventTypeValue)}
                                    onChange={(value) =>
                                        setValue('eventType', Number(value) as CalendarEventType)
                                    }
                                    options={eventTypeOptions}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label>{t('superAdmin.data.bookings.columns.type')}</Label>
                                <ESingleSelectPicker
                                    value={String(documentTypeValue)}
                                    onChange={(value) =>
                                        setValue('documentType', Number(value) as DocumentType)
                                    }
                                    options={documentTypeOptions}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label>{t('superAdmin.data.bookings.columns.status')}</Label>
                                <ESingleSelectPicker
                                    value={String(bookingStatusValue)}
                                    onChange={(value) =>
                                        setValue('bookingStatus', Number(value) as BookingStatus)
                                    }
                                    options={bookingStatusOptions}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="numberOfRooms">
                                    {t('superAdmin.data.bookings.detail.rooms')}
                                </Label>
                                <Input
                                    id="numberOfRooms"
                                    type="number"
                                    min={0}
                                    {...register('numberOfRooms')}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="totalAmount">
                                    {t('superAdmin.data.bookings.columns.amount')}
                                </Label>
                                <Input
                                    id="totalAmount"
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    {...register('totalAmount')}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <Label htmlFor="remarks">
                                    {t('superAdmin.data.bookings.detail.remarks')}
                                </Label>
                                <Input id="remarks" {...register('remarks')} />
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={isActiveValue}
                                    onChange={(event) => setValue('isActive', event.target.checked)}
                                />
                                {t('superAdmin.data.bookings.detail.active')}
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={hasAccommodationValue}
                                    onChange={(event) =>
                                        setValue('hasAccommodation', event.target.checked)
                                    }
                                />
                                {t('superAdmin.data.bookings.detail.hasAccommodation')}
                            </label>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                                {t('elements.buttons.cancel')}
                            </Button>
                            <Button type="submit">{t('superAdmin.data.bookings.edit.continue')}</Button>
                        </DialogFooter>
                    </form>
                ) : (
                    <>
                        <div className="flex flex-col gap-1.5">
                            <Label>{t('superAdmin.data.bookings.edit.confirmLabel')}</Label>
                            <Input
                                value={confirmText}
                                onChange={(event) => setConfirmText(event.target.value)}
                                placeholder={t('superAdmin.data.bookings.edit.confirmPlaceholder')}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setStep('form')}>
                                {t('elements.buttons.back')}
                            </Button>
                            <Button
                                disabled={confirmText !== confirmWord || isSaving}
                                onClick={() => void handleConfirmSave()}
                            >
                                {t('superAdmin.data.bookings.edit.confirmAction')}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SuperAdminBookingEditDialog;
